import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { usePokemonConfig, PokemonConfig } from "@/hooks/usePokemonConfig";
import { Airplay } from "lucide-react";
import { FooterLabel } from "@/components/FooterLabel";
import { optimizedImage } from "@/lib/image";

const Index = () => {
  const { data: pokemonList = [], isLoading } = usePokemonConfig();
  const [votedName, setVotedName] = useState<string | null>(null);
  const [showThank, setShowThank] = useState(false);

  const handleVote = async (name: string) => {
    if (showThank) return;
    setVotedName(name);
    setShowThank(true);

    await supabase.from("votes").insert({ pokemon_name: name });

    setTimeout(() => {
      setShowThank(false);
      setVotedName(null);
    }, 2500);
  };

  const votedPokemon = pokemonList.find((p) => p.name === votedName);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🔴</span>
          <h1 className="text-xl font-bold text-foreground">Escolha seu Pokémon favorito</h1>
        </div>
        <span className="text-sm text-muted-foreground">Toque para Escolher!</span>
      </header>

      <main className="max-w-6xl mx-auto p-6 relative">
        {showThank && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 animate-fade-in">
            <div className="bg-card rounded-2xl p-8 flex flex-col items-center gap-4 shadow-xl max-w-sm mx-4 animate-pop-in">
              {votedPokemon?.sprite_url && (
                <img
                  src={optimizedImage(votedPokemon.sprite_url, { width: 288, height: 288 })}
                  alt={votedName || ""}
                  className="w-36 h-36 object-contain"
                />
              )}
              <p className="text-lg font-bold text-foreground text-center">
                Obrigado pela sua votação!
              </p>
              <p className="text-sm text-muted-foreground text-center">
                Você votou em <span className="font-semibold text-primary">{votedName}</span>
              </p>
              <p className="text-xs text-muted-foreground">Preparando para o próximo voto...</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {pokemonList.map((p, idx) => (
            <button
              key={p.id}
              onClick={() => handleVote(p.name)}
              disabled={showThank}
              className="bg-card rounded-xl p-4 flex flex-col items-center gap-2 transition-all duration-200 border border-border hover:border-muted-foreground/30 hover:shadow-md active:scale-95 disabled:opacity-60 disabled:pointer-events-none"
            >
              {p.sprite_url && (
                <img
                  src={optimizedImage(p.sprite_url, { width: 192, height: 192 })}
                  alt={p.name}
                  width={96}
                  height={96}
                  loading={idx < 5 ? "eager" : "lazy"}
                  decoding="async"
                  {...(idx === 0 ? { fetchPriority: "high" as const } : {})}
                  className="w-24 h-24 object-contain"
                />
              )}
              <span className="text-sm font-medium text-foreground">{p.name}</span>
            </button>
          ))}
        </div>

        <div className="mt-6 flex justify-center">
          <Link
            to="/ranking"
            state={{ fromVoting: true }}
            className="inline-block"
          >
            <div className="border-2 border-[#e74c5e] bg-[#e74c5e]/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-[#e74c5e]/20 hover:shadow-lg transition-all duration-200 cursor-pointer">
              <Airplay className="w-5 h-5 text-[#e74c5e]" />
              <div className="text-center">
                <p className="font-bold text-foreground text-sm">Confira as escolhas</p>
                <p className="text-xs text-muted-foreground">Veja ao vivo na TV →</p>
              </div>
            </div>
          </Link>
        </div>
      </main>
      <FooterLabel variant="voting" />
    </div>
  );
};

export default Index;
